import { type ReactNode } from "react"

// Accent tints per section for icon boxes.
// Shared by person-details (existing sections) and the new PMB v2 sections.
export const SECTION_STYLES = {
  talking_points: {
    hex: "#6c5ce7",
    bg: "rgba(108,92,231,0.10)",
    shadow: "0 1px 3px rgba(108,92,231,0.12)"
  },
  key_account_priorities: {
    hex: "#0984e3",
    bg: "rgba(9,132,227,0.10)",
    shadow: "0 1px 3px rgba(9,132,227,0.12)"
  },
  know_your_prospect: {
    hex: "#e17055",
    bg: "rgba(225,112,85,0.10)",
    shadow: "0 1px 3px rgba(225,112,85,0.12)"
  },
  strategic_agenda: {
    hex: "#00b894",
    bg: "rgba(0,184,148,0.10)",
    shadow: "0 1px 3px rgba(0,184,148,0.12)"
  },
  about: {
    hex: "#6c5ce7",
    bg: "rgba(108,92,231,0.10)",
    shadow: "0 1px 3px rgba(108,92,231,0.12)"
  },
  account_brief: {
    hex: "#636e72",
    bg: "rgba(99,110,114,0.10)",
    shadow: "0 1px 3px rgba(99,110,114,0.12)"
  },
  coaching_pack: {
    hex: "#f59e0b",
    bg: "rgba(245,158,11,0.10)",
    shadow: "0 1px 3px rgba(245,158,11,0.12)"
  },
  tech_stack: {
    hex: "#0891b2",
    bg: "rgba(8,145,178,0.10)",
    shadow: "0 1px 3px rgba(8,145,178,0.12)"
  },
  industry_trends: {
    hex: "#059669",
    bg: "rgba(5,150,105,0.10)",
    shadow: "0 1px 3px rgba(5,150,105,0.12)"
  },
  social_media: {
    hex: "#7c3aed",
    bg: "rgba(124,58,237,0.10)",
    shadow: "0 1px 3px rgba(124,58,237,0.12)"
  }
}

export function SectionHeader({
  icon,
  title,
  subtitle,
  styleKey,
  after
}: {
  icon: ReactNode
  title: string
  subtitle: string
  styleKey: keyof typeof SECTION_STYLES
  after?: string
}) {
  const s = SECTION_STYLES[styleKey]
  return (
    <div className="flex items-start gap-3 mb-4">
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: s.bg, color: s.hex, boxShadow: s.shadow }}
      >
        {icon}
      </span>
      <div>
        <p className="text-[15px] font-semibold text-dark-2 dark:text-dark-6">
          {title}
          {after && (
            <span className="ml-1" style={{ color: s.hex }}>
              {after}
            </span>
          )}
        </p>
        <p className="text-[11px] text-gray-500 dark:text-text-gray-300 mt-0.5">
          {subtitle}
        </p>
      </div>
    </div>
  )
}

export function SectionShell({
  id,
  styleKey,
  icon,
  title,
  subtitle,
  isLoading,
  error,
  isEmpty,
  children
}: {
  id: string
  styleKey: keyof typeof SECTION_STYLES
  icon: ReactNode
  title: string
  subtitle: string
  isLoading: boolean
  error?: string | null
  isEmpty: boolean
  emptyText?: string
  children?: ReactNode
}) {
  // Omit the section entirely unless it has real content: no loading flash and
  // no empty/error placeholder. The nav pill is gated the same way upstream.
  if (isLoading || error || isEmpty) return null
  return (
    <div
      id={id}
      className="py-8 border-b border-light-border/30 dark:border-border/30"
    >
      <SectionHeader
        icon={icon}
        title={title}
        subtitle={subtitle}
        styleKey={styleKey}
      />
      {children}
    </div>
  )
}
